/**
 * Places RainViewer says it watches and doesn't.
 *
 * The coverage mask (`lib/coverage-layer.ts`) is the app's answer to "is this
 * map empty because it is dry, or because nobody is looking?" — but the mask is
 * RainViewer's own account of itself, and it can be wrong in the one direction
 * that matters. A registered radar that publishes a footprint and no returns
 * reads as *covered*: the scrim leaves it bright, the radar draws nothing, and a
 * viewer standing in it gets the most confident possible rendering of "clear",
 * which is worse than a blank map. It is the exact failure the scrim exists to
 * prevent, arriving through the scrim's own data.
 *
 * So this is a hand-maintained list of regions where that has been observed, OR'd
 * into the scrim and surfaced as a plain-language notice. It is deliberately
 * small, deliberately manual, and deliberately dated: every entry records when it
 * was last checked, because the fix for one of these is upstream and could land
 * any week. Re-check before trusting an old `verifiedOn`.
 *
 * **How to verify an entry.** Do not eyeball the map — an empty tile at a quiet
 * hour proves nothing. Pull every frame in the current loop, count non-transparent
 * pixels inside the region, and compare against a neighbouring area in the same
 * frames. A real gap is zero across the whole loop while its neighbours are not.
 *
 * The polygons are coarse region outlines, not borders. They exist to answer
 * "is the viewer in the affected area", and a few kilometres either way changes
 * nothing about the answer.
 */

/** A region RainViewer claims to cover but returns no data for. */
export interface CoverageGap {
  readonly id: string;
  /** What to call the region in the notice. */
  readonly name: string;
  /** Why it is listed, in the viewer's terms — not ours. */
  readonly note: string;
  /** Where someone standing in the gap can get real radar instead. */
  readonly localSource?: { readonly label: string; readonly url: string };
  /** ISO date the emptiness was last confirmed against the live tiles. */
  readonly verifiedOn: string;
  /** Coarse outline as `[longitude, latitude]`, implicitly closed. */
  readonly outline: readonly (readonly [number, number])[];
}

export const COVERAGE_GAPS: readonly CoverageGap[] = [
  {
    id: 'costa-rica',
    name: 'Costa Rica',
    note: 'RainViewer maps this area as covered — by a range ring centred in Nicaragua — but its mosaic carries no returns from it. An empty map here means unobserved, not dry.',
    localSource: {
      label: 'Instituto Meteorológico Nacional radar',
      url: 'https://www.imn.ac.cr/radar',
    },
    verifiedOn: '2026-08-14',
    // Roughly the national outline, anticlockwise from the north-west corner:
    // down the Pacific side over Nicoya and Osa, up the Panamanian border from
    // Punta Burica, north along the Caribbean coast, then west along the
    // Nicaraguan border. Winding is nothing MapLibre or the containment test
    // care about, which is exactly why it is worth stating — RFC 7946 §3.1.6
    // asks for anticlockwise exterior rings, so this is the order that keeps
    // the comment and the spec agreeing with the data.
    outline: [
      [-85.95, 10.9],
      [-85.85, 10.33],
      [-85.11, 9.56],
      [-84.16, 9.4],
      [-83.86, 9.25],
      [-83.73, 8.58],
      [-83.28, 8.37],
      [-82.88, 8.05],
      [-82.94, 9.03],
      [-82.56, 9.53],
      // Just off Limón rather than on it: a port city sits on the coastline,
      // and a vertex laid over the town is a coin flip about whether it is in.
      [-82.95, 10.05],
      [-83.67, 10.93],
      [-83.7, 10.75],
      [-84.2, 10.78],
      [-84.71, 11.02],
      [-85.71, 11.09],
    ],
  },
];

/**
 * The gap containing a point, if any.
 *
 * Regions do not overlap, so the first hit is the answer.
 */
export function coverageGapAt(
  longitude: number,
  latitude: number,
  gaps: readonly CoverageGap[] = COVERAGE_GAPS,
): CoverageGap | null {
  return gaps.find((gap) => contains(gap.outline, longitude, latitude)) ?? null;
}

/**
 * Ray casting, counting crossings of a horizontal line through the point.
 *
 * Nothing here crosses the antimeridian, so longitude needs no wrapping — if an
 * entry ever does, split it into two polygons rather than teaching this about
 * the seam.
 */
function contains(
  outline: readonly (readonly [number, number])[],
  longitude: number,
  latitude: number,
): boolean {
  let inside = false;

  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const [xi, yi] = outline[i]!;
    const [xj, yj] = outline[j]!;

    // Only edges straddling the point's latitude can be crossed. The
    // half-open comparison keeps a vertex exactly on the ray from counting
    // twice, which would flip `inside` back and report a hit as a miss.
    const straddles = yi > latitude !== yj > latitude;
    if (!straddles) continue;

    const crossingLongitude = xi + ((latitude - yi) / (yj - yi)) * (xj - xi);
    if (longitude < crossingLongitude) inside = !inside;
  }

  return inside;
}

/**
 * Just the shape MapLibre needs, spelled out here rather than imported.
 * `@types/geojson` reaches this package only as a transitive dependency of
 * maplibre-gl, and depending on a name we don't declare is how a build breaks on
 * someone else's version bump.
 */
export interface CoverageGapFeatureCollection {
  readonly type: 'FeatureCollection';
  readonly features: readonly {
    readonly type: 'Feature';
    readonly id: string;
    readonly properties: { readonly id: string; readonly name: string };
    readonly geometry: { readonly type: 'Polygon'; readonly coordinates: number[][][] };
  }[];
}

/**
 * The gaps as a GeoJSON `FeatureCollection`, for the fill layer that adds them
 * to the scrim. Built fresh on each call — MapLibre takes ownership of what it
 * is handed, and a shared literal would be mutable across map rebuilds.
 */
export function coverageGapsGeoJson(
  gaps: readonly CoverageGap[] = COVERAGE_GAPS,
): CoverageGapFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: gaps.map((gap) => ({
      type: 'Feature',
      id: gap.id,
      properties: { id: gap.id, name: gap.name },
      geometry: {
        type: 'Polygon',
        // GeoJSON rings must repeat their first position as the last.
        coordinates: [[...gap.outline.map(([lon, lat]) => [lon, lat]), [...gap.outline[0]!]]],
      },
    })),
  };
}
